import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { createTaskAtom } from '../../atoms/v0/taskAtoms';
import { useTranslation } from 'react-i18next';

export const TaskCreate: React.FC = () => {
  const { t } = useTranslation(['v3/tasks', 'common']);
  const navigate = useNavigate();
  const [, createTask] = useAtom(createTaskAtom);

  // 表单状态
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [vital, setVital] = useState(false);
  const [urgent, setUrgent] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');

  // 处理返回
  const handleBack = () => {
    navigate(-1);
  };

  // 处理提交
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      alert(t('v3/tasks:validation.titleRequired'));
      return;
    }

    // 处理截止日期
    let dueAt = null;
    if (dueDate) {
      const dateObj = new Date(dueDate);
      if (dueTime) {
        const [hours, minutes] = dueTime.split(':').map(Number);
        dateObj.setHours(hours, minutes);
      }
      dueAt = dateObj.toISOString();
    }

    const success = await createTask({
      title,
      notes: notes || undefined,
      vital,
      urgent,
      dueAt: dueAt || undefined,
    });

    if (success) {
      navigate(-1);
    }
  };

  return (
    <div className="p-4 pb-20 font-ios">
      {/* 返回按钮 */}
      <div className="mb-4">
        <button
          onClick={handleBack}
          className="flex items-center text-ios-blue dark:text-ios-dark-blue"
        >
          <svg className="w-5 h-5 mr-1" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>{t('common:actions.cancel')}</span>
        </button>
      </div>

      {/* 表单 */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-white dark:bg-ios-dark-gray6 rounded-ios-lg overflow-hidden border border-ios-gray5 dark:border-ios-dark-gray5 shadow-ios dark:shadow-ios-dark p-4">
          <h1 className="text-ios-title3 font-semibold mb-4">{t('v3/tasks:createTask')}</h1>

          <div className="space-y-4">
            <div>
              <label className="block text-ios-footnote text-ios-gray dark:text-ios-dark-gray mb-1">
                {t('v3/tasks:fields.title')} *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('v3/tasks:placeholders.title')}
                required
                className="w-full p-2 bg-ios-gray6 dark:bg-ios-dark-gray5 rounded-ios-md border border-ios-gray5 dark:border-ios-dark-gray4 focus:outline-none focus:ring-2 focus:ring-ios-blue dark:focus:ring-ios-dark-blue"
              />
            </div>

            <div>
              <label className="block text-ios-footnote text-ios-gray dark:text-ios-dark-gray mb-1">
                {t('v3/tasks:fields.notes')}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('v3/tasks:placeholders.notes')}
                className="w-full p-2 bg-ios-gray6 dark:bg-ios-dark-gray5 rounded-ios-md border border-ios-gray5 dark:border-ios-dark-gray4 focus:outline-none focus:ring-2 focus:ring-ios-blue dark:focus:ring-ios-dark-blue min-h-[6.25rem]"
              />
            </div>

            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={vital}
                  onChange={(e) => setVital(e.target.checked)}
                  className="w-5 h-5 rounded-md text-ios-blue dark:text-ios-dark-blue focus:ring-ios-blue dark:focus:ring-ios-dark-blue"
                />
                <span className="ml-2">{t('v3/tasks:priority.vital')}</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={urgent}
                  onChange={(e) => setUrgent(e.target.checked)}
                  className="w-5 h-5 rounded-md text-ios-blue dark:text-ios-dark-blue focus:ring-ios-blue dark:focus:ring-ios-dark-blue"
                />
                <span className="ml-2">{t('v3/tasks:priority.urgent')}</span>
              </label>
            </div>

            <div>
              <label className="block text-ios-footnote text-ios-gray dark:text-ios-dark-gray mb-1">
                {t('v3/tasks:fields.dueDate')}
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full p-2 bg-ios-gray6 dark:bg-ios-dark-gray5 rounded-ios-md border border-ios-gray5 dark:border-ios-dark-gray4 focus:outline-none focus:ring-2 focus:ring-ios-blue dark:focus:ring-ios-dark-blue"
              />
            </div>

            {dueDate && (
              <div>
                <label className="block text-ios-footnote text-ios-gray dark:text-ios-dark-gray mb-1">
                  {t('v3/tasks:fields.dueTime')}
                </label>
                <input
                  type="time"
                  value={dueTime}
                  onChange={(e) => setDueTime(e.target.value)}
                  className="w-full p-2 bg-ios-gray6 dark:bg-ios-dark-gray5 rounded-ios-md border border-ios-gray5 dark:border-ios-dark-gray4 focus:outline-none focus:ring-2 focus:ring-ios-blue dark:focus:ring-ios-dark-blue"
                />
              </div>
            )}
          </div>
        </div>

        <button
          type="submit"
          className="w-full py-3 bg-ios-blue dark:bg-ios-dark-blue text-white rounded-full text-ios-body font-medium"
        >
          {t('v3/tasks:actions.create')}
        </button>
      </form>
    </div>
  );
};
